import {z} from "zod";

/**
 * postMessage contract between the ChatGPT plugin shell (an MCP App) and the
 * Sprout web app it frames. The shell only sends to the app's exact origin;
 * the app accepts messages only from its parent window.
 */
export const embedChannel = "sprout.embed.v1";

/** A resolved CSS color from ChatGPT's theme, such as `rgb(14, 14, 14)`. */
export const embedColorSchema = z.string().regex(/^(rgba?|hsla?|oklch|oklab|lab|lch|color)\([0-9a-z.,%\s/+-]{1,80}\)$/);

/**
 * App-relative route, never an absolute or protocol-relative URL. URL parsers
 * read `\` as `/` and drop tabs and line breaks, so `/\host` and `/<tab>/host`
 * would leave the app.
 */
export const embedPathSchema = z
  .string()
  .max(2048)
  .regex(/^\/(?!\/)[^\\\t\n\r]*$/, "Use an app-relative path.");

export const embedAppMessageSchema = z.discriminatedUnion("type", [
  z.object({channel: z.literal(embedChannel), type: z.literal("ready")}),
  z.object({channel: z.literal(embedChannel), type: z.literal("session-needed")}),
  z.object({channel: z.literal(embedChannel), type: z.literal("open-link"), url: z.string().url().startsWith("https://")}),
  /** A question for ChatGPT's own composer, which replaces Sprout's chat inside ChatGPT. */
  z.object({channel: z.literal(embedChannel), type: z.literal("ask"), text: z.string().trim().min(1).max(2000)}),
  /** The screen the person is looking at, with what it shows, shared with ChatGPT as background context. */
  z.object({channel: z.literal(embedChannel), type: z.literal("context"), path: embedPathSchema, title: z.string().trim().min(1).max(80), summary: z.string().trim().min(1).max(240).optional()})
]);
export type EmbedAppMessage = z.infer<typeof embedAppMessageSchema>;

export const embedShellMessageSchema = z.discriminatedUnion("type", [
  z.object({channel: z.literal(embedChannel), type: z.literal("session"), code: z.string().min(1).max(200), path: embedPathSchema.optional()}),
  z.object({channel: z.literal(embedChannel), type: z.literal("navigate"), path: embedPathSchema}),
  z.object({channel: z.literal(embedChannel), type: z.literal("unavailable"), message: z.string().max(240)}),
  /** ChatGPT's theme and surface colors, which the app follows so it reads as part of ChatGPT. */
  z.object({channel: z.literal(embedChannel), type: z.literal("theme"), theme: z.enum(["light", "dark"]), background: embedColorSchema.optional(), surface: embedColorSchema.optional()})
]);
export type EmbedShellMessage = z.infer<typeof embedShellMessageSchema>;

/** Route the shell frames; the app treats it as the embedded entry point. */
export const embedEntryPath = "/embed/chatgpt";

export const assistantEntryParam = "from";
export const assistantEntryValue = "assistant";
/** Fragment key for the one-time code a plugin link carries; browsers never send fragments to servers or in referrers. */
export const assistantHandoffParam = "handoff";

/**
 * A browser link from the plugin. The app skips its quiz for people who
 * already chose Sprout in their assistant; a one-time code signs in a browser
 * that does not share the assistant's sign-in.
 */
export function assistantEntryUrl(path: string, origin: string, handoffCode?: string | null): string {
  const app = new URL(origin);
  const requested = new URL(path, app);
  // A path that resolves to another site opens the app's home instead, so the code never leaves Sprout.
  const url = requested.origin === app.origin ? requested : new URL(embedViews.home.path, app);
  url.searchParams.set(assistantEntryParam, assistantEntryValue);
  if (handoffCode) url.hash = new URLSearchParams({[assistantHandoffParam]: handoffCode}).toString();
  return url.toString();
}

/** Screens ChatGPT can bring up in the Sprout app it has open. */
export const embedViews = {
  home: {path: "/home", title: "Today"},
  spending: {path: "/insights", title: "Spending"},
  transactions: {path: "/insights/transactions", title: "Spending records"},
  accounts: {path: "/accounts", title: "Accounts"}
} as const;
export type EmbedView = keyof typeof embedViews;
export const embedViewSchema = z.enum(Object.keys(embedViews) as [EmbedView, ...EmbedView[]]);

/**
 * A screen with its subject and filters: an account or category opens that
 * account's or category's page; on spending records they narrow the list.
 */
export type EmbedScreen = {
  view: EmbedView;
  accountId?: string | undefined;
  category?: string | undefined;
  search?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  currency?: string | undefined;
};

/** Spending-record filters and their query parameters. */
const recordFilters = {search: "merchant", category: "category", accountId: "account", from: "from", to: "to", currency: "currency"} as const;

export function embedScreenPath(screen: EmbedScreen): string {
  if (screen.view === "accounts" && screen.accountId) return `/accounts/${encodeURIComponent(screen.accountId)}`;
  if (screen.view === "spending" && screen.category) return `/insights/categories/${encodeURIComponent(screen.category)}`;
  if (screen.view !== "transactions") return embedViews[screen.view].path;
  const query = new URLSearchParams();
  for (const [key, parameter] of Object.entries(recordFilters)) {
    const value = screen[key as keyof typeof recordFilters];
    if (value) query.set(parameter, value);
  }
  return query.size ? `${embedViews.transactions.path}?${query}` : embedViews.transactions.path;
}

/** Reads an app route back into the screen it shows; null for routes outside the embed's screens. */
export function embedScreenFromPath(path: string): EmbedScreen | null {
  const url = new URL(path, "https://sprout.invalid");
  const segments = url.pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));
  if (url.pathname === embedViews.transactions.path) {
    const screen: EmbedScreen = {view: "transactions"};
    for (const [key, parameter] of Object.entries(recordFilters)) {
      const value = url.searchParams.get(parameter);
      if (value) screen[key as keyof typeof recordFilters] = value;
    }
    return screen;
  }
  if (segments[0] === "insights" && segments[1] === "categories" && segments[2]) return {view: "spending", category: segments[2]};
  if (segments[0] === "accounts" && segments[1]) return {view: "accounts", accountId: segments[1]};
  const view = (Object.keys(embedViews) as EmbedView[]).find((key) => embedViews[key].path === url.pathname);
  return view ? {view} : null;
}
